/**
 * Clear Cart Page (layoutless)
 *
 * Silent route that clears the cart session and redirects.
 * Used post-checkout or for campaign resets.
 *
 * Supports an optional `?redirect=` query parameter for the target URL
 * (defaults to `/`). Only same-origin paths are allowed to prevent open redirects.
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCartContext } from '@/lib/context/cart-context';

function getSafeRedirect(raw: string | null): string {
  if (!raw) return '/';
  try {
    const url = new URL(raw, globalThis.location.origin);
    if (url.origin !== globalThis.location.origin) return '/';
    return url.pathname + url.search + url.hash;
  } catch {
    return '/';
  }
}

export function ClearCartPage() {
  const { clearCart } = useCartContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    clearCart();
    const target = getSafeRedirect(searchParams.get('redirect'));
    navigate(target, { replace: true });
  }, [clearCart, navigate, searchParams]);

  return null;
}
